import React, { useState, useEffect } from 'react'

import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { useIllustrationsApi } from 'src/api/db/apiProductImages'
import { ProductImage } from 'src/shared/components/ProductImage'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
// import { Design_ } from 'src/api/db/apiDesigns'
import { Button } from 'src/shared/components/ui/button'
import { Dialog, DialogContent } from 'src/shared/components/ui/dialog'

export const ProductImageSection = ({
  chosen_product_variant_color_,
  selectedIllustration,
  setSelectedIllustration,
  product_id,
}: {
  chosen_product_variant_color_: ProductVariantColor_
  selectedIllustration?: any
  setSelectedIllustration?: (props: any) => void
  product_id?: number
}) => {
  // Props kept for backward compatibility but not used with Carousel
  void selectedIllustration
  void setSelectedIllustration
  void product_id

  const isAdmin = useIsAdmin()
  const { updateIllustrationsBulk } = useIllustrationsApi({})

  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set())
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  )

  let illustrations = chosen_product_variant_color_?.product_image ?? []
  illustrations = illustrations.sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  )

  // Filter out illustrations with failed image URLs or missing URLs
  const validIllustrations = illustrations.filter(
    (illustration) =>
      illustration?.image_url && !failedImageUrls.has(illustration.image_url)
  )

  // Validate images on mount and when illustrations change
  useEffect(() => {
    const validateImages = async () => {
      const newFailedUrls = new Set<string>()
      const imageUrls = illustrations
        .map((i) => i?.image_url)
        .filter((url): url is string => Boolean(url))

      await Promise.all(
        imageUrls.map(async (imageUrl) => {
          return new Promise<void>((resolve) => {
            const img = new Image()
            img.onload = () => resolve()
            img.onerror = () => {
              newFailedUrls.add(imageUrl)
              resolve()
            }
            img.src = imageUrl
            // Timeout after 5 seconds
            setTimeout(() => {
              if (!img.complete) {
                newFailedUrls.add(imageUrl)
              }
              resolve()
            }, 5000)
          })
        })
      )

      setFailedImageUrls(newFailedUrls)
    }

    validateImages()
  }, [
    illustrations
      .map((i) => i?.id)
      .filter(Boolean)
      .join(','),
    illustrations
      .map((i) => i?.image_url)
      .filter(Boolean)
      .join(','),
  ])

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const handleShowFirst =
    (clickedIndex: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const reordered = [
        validIllustrations[clickedIndex],
        ...validIllustrations.filter((_, i) => i !== clickedIndex),
      ]
      const updates = reordered.map((img, idx) => ({ id: img.id, order: idx }))
      updateIllustrationsBulk.mutate(updates)
    }

  const selectedImage =
    selectedImageIndex !== null ? validIllustrations[selectedImageIndex] : null

  return (
    <div className="relative">
      {validIllustrations?.length > 0 ? (
        <>
          {/* Masonry Grid - Single Column */}
          <div className="flex flex-col gap-4">
            {validIllustrations.map((illustration, index) => {
              return (
                <div
                  key={illustration.id}
                  className="group relative cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleImageClick(index)}
                >
                  <ProductImage
                    image_url={illustration?.image_url || ''}
                    // design={(illustration as any)?.design?.[0] as Design_}
                    containerClassName={'rounded-2xl'}
                    imageClassName={'object-contain w-full'}
                  />
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={handleShowFirst(index)}
                      disabled={updateIllustrationsBulk.isPending}
                    >
                      Show first
                    </Button>
                  )}
                </div>
              )
            })}
          </div>

          {/* Image Dialog */}
          <Dialog
            open={selectedImageIndex !== null}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedImageIndex(null)
              }
            }}
          >
            <DialogContent className="w-[80vw] max-w-none max-h-[90vh] overflow-y-auto p-1">
              {selectedImage && (
                <div className="w-full">
                  <ProductImage
                    onImageLoad={() => {}}
                    image_url={selectedImage?.image_url || ''}
                    containerClassName={'rounded-xl w-full'}
                    imageClassName={'object-contain w-full h-auto'}
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="h-[500px] flex items-center justify-center rounded-md bg-gray-100">
          <p className="text-sm text-foreground/60">No images available</p>
        </div>
      )}
    </div>
  )
}
